import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Clock, CheckCircle, ChevronRight, X } from "lucide-react";
import ServiceQuoteButton from "@/components/ServiceQuoteButton";

const FEATURE_BULLETS = [
  "Professionel udførelse hos dig",
  "Erfaren mekaniker",
  "Garanti på reservedele og arbejde",
  "Ingen kørselsgebyr",
];

/**
 * Modal that opens when a customer taps a service card on the landing page.
 */
const ServiceDetailDialog = ({ service, open, onOpenChange }) => {
  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="
          public-site p-0 gap-0 overflow-hidden border border-white/10 bg-[#0A0A0A] text-white
          max-w-[92vw] sm:max-w-[78vw] md:max-w-[70vw] lg:max-w-3xl
          w-[92vw] sm:w-[78vw] md:w-[70vw] lg:w-auto
          max-h-[88vh] flex flex-col rounded-sm
        "
        data-testid={`service-detail-${service.id}`}
      >
        <DialogTitle className="sr-only">{service.name}</DialogTitle>

        {/* Image */}
        <div className="relative h-44 sm:h-56 md:h-64 flex-shrink-0 overflow-hidden">
          <img
            src={
              service.image_url ||
              "https://images.pexels.com/photos/8478233/pexels-photo-8478233.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            }
            alt={service.name}
            className="w-full h-full object-cover"
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(0,0,0,0) 20%, rgba(10,10,10,0.85) 100%)",
            }}
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Luk"
            className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-black/60 hover:bg-black/80 backdrop-blur-md rounded-sm text-white transition-colors"
            data-testid="service-detail-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 sm:py-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <h2 className="text-xl sm:text-2xl font-black text-white leading-tight" style={{ fontFamily: "Saira" }}>
              {service.name}
            </h2>
            {service.quote_required ? (
              service.price > 0 ? (
                <span className="text-2xl sm:text-3xl font-black text-white whitespace-nowrap" style={{ fontFamily: "Saira" }}>
                  Fra {service.price} kr
                </span>
              ) : (
                <span className="text-sm sm:text-base font-black text-[#00F0FF] tracking-wider whitespace-nowrap" style={{ fontFamily: "Saira" }}>
                  FÅ ET TILBUD
                </span>
              )
            ) : (
              <span className="text-2xl sm:text-3xl font-black text-white whitespace-nowrap" style={{ fontFamily: "Saira" }}>
                {service.price} kr
              </span>
            )}
          </div>

          <p className="text-[#A0AEC0] text-sm sm:text-base leading-relaxed whitespace-pre-line mb-5">
            {service.description}
          </p>

          <ul className="space-y-2 mb-5">
            {FEATURE_BULLETS.map((bullet) => (
              <li key={bullet} className="flex items-start gap-2 text-sm text-white">
                <CheckCircle className="w-4 h-4 text-[#00F0FF] mt-0.5 flex-shrink-0" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2 text-[#A0AEC0] text-sm pt-3 border-t border-white/10">
            <Clock className="w-4 h-4" />
            <span>Varighed ca. {service.duration_minutes} min</span>
          </div>
        </div>

        {/* Sticky CTA bar */}
        <div className="flex-shrink-0 border-t border-white/10 bg-white/[0.03] px-5 sm:px-7 py-4">
          {service.quote_required ? (
            <div onClick={(e) => e.stopPropagation()}>
              <ServiceQuoteButton service={service} className="w-full py-5" />
            </div>
          ) : (
            <Link to={`/book?service=${service.id}`} onClick={() => onOpenChange(false)} data-testid={`book-from-detail-link-${service.id}`}>
              <Button
                className="w-full bg-[#00F0FF] hover:bg-[#00D0E0] text-black font-bold rounded-full py-5 text-base glow-cyan"
                data-testid={`book-from-detail-${service.id}`}
              >
                Book tid <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceDetailDialog;
